import { useOfflineAudiosQuery } from "@src/data/queries/offlineAudios";
import { devMode, getHost } from "@src/env";
import type { MessageFromServiceWorker } from "@src/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Indicator } from "@src/components/Indicator";
import { LinkChanger } from "@src/components/LinkChanger";
import { ProxyIndicator } from "@src/components/ProxyIndicator";
import { TestButtons } from "@src/components/TestButtons";

import { useMeuPlayState } from "@src/states/meuplay";
import { useOfflineModeState } from "@src/states/offline_mode";
import { usePlayerState } from "@src/states/player";

import { upsertRadioSession } from "@src/services/api/session";
import { player } from "@src/services/player";

import { useTabFocusRecovery } from "@src/hooks/useTabFocusRecovery";

import { checkConnection } from "@src/utils/checkConnection";
import { getManifestUrl } from "@src/utils/getManifestUrl";
import { enterOfflineMode } from "@src/utils/offline/enterOfflineMode";
import { renewPlayedUrls } from "@src/utils/offline/renewPlayedUrls";
import { exchangeMessage } from "@src/utils/serviceWorker/exchangeMessage";

import "./style.less";
import type { OfflineModeIndicatorProps } from "./types";

export function OfflineModeIndicator({ radio }: OfflineModeIndicatorProps) {
  function afterRemoveFromCache(eventData: MessageFromServiceWorker) {
    if (eventData.type !== "RESPONSE_REMOVE_FROM_CACHE") return;

    const { cachedUrls, removedUrls } = eventData.payload;
    if (radio.offline_mode && removedUrls.length > 0) {
      const avoidUrls = new Set([...cachedUrls, ...removedUrls]);
      offlineAudiosQuery.refetch().then(({ data }) => {
        if (data?.cacheableAudioLinks) {
          const urlsToCache = data.cacheableAudioLinks
            .filter((url) => !avoidUrls.has(url))
            .slice(0, removedUrls.length);
          if (urlsToCache.length > 0) {
            exchangeMessage({
              idRadio: radio.name,
              type: "ADD_TO_CACHE",
              payload: {
                cacheName: "offlineAudios",
                urls: urlsToCache,
              },
            }).then(afterAddToCache);
            useOfflineModeState.setState({ playedUrls: new Set() });
          }
        }
      });
    }
  }

  // Detecta queda/retorno de internet via eventos nativos do browser (instantâneo)
  useEffect(() => {
    const onOffline = () => {
      console.log("[window:offline] isOnline set to false");
      useOfflineModeState.setState({ isOnline: false });
    };
    const onOnline = () => {
      // O evento 'online' pode disparar com rede local mas sem internet real;
      // confirmar com checkConnection antes de declarar online.
      checkConnection(
        () => {
          console.log("[window:online] isOnline set to true");
          useOfflineModeState.setState({ isOnline: true });
        },
        () => {
          console.log("[window:online] evento disparado, mas sem internet real");
        },
      );
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // Detecta se a conexão de internet está online ou offline
  const [monitoringConnection, setMonitoringConnection] = useState(false);
  useEffect(() => {
    if (monitoringConnection) {
      const interval = setInterval(() => {
        if (!useOfflineModeState.getState().enableConnectionMonitor) {
          // console.log("Connection Monitor is disabled");
          return;
        }
        checkConnection(
          () => {
            renewPlayedUrls(radio.name, afterRemoveFromCache); // REMOVE_FROM_CACHE
            if (!useOfflineModeState.getState().isOnline) {
              console.log("[OfflineModeIndicator] isOnline set to true");
              useOfflineModeState.setState({ isOnline: true });
            }
          },
          () => {
            if (useOfflineModeState.getState().isOnline) {
              console.log("[OfflineModeIndicator] isOnline set to false");
              useOfflineModeState.setState({ isOnline: false });
            }
          },
        );
      }, 5_000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitoringConnection, radio.name]);

  // Inicia a música offline, se aplicável
  const { isOnline, error, color, cachedUrls, reason } = useOfflineModeState();
  const currentTrackId = usePlayerState((state) => state.currentTrackId);
  const isOfflinePlaying = currentTrackId === "offline";
  const queryClient = useQueryClient();
  useTabFocusRecovery(queryClient);
  useEffect(() => {
    console.log(
      "isOnline=",
      isOnline,
      "isOfflinePlaying=",
      isOfflinePlaying,
      "cachedUrls.size=",
      cachedUrls.size,
    );
    if (!isOnline && !isOfflinePlaying && cachedUrls.size > 0) {
      enterOfflineMode(queryClient, "E");
    }
  }, [isOnline, isOfflinePlaying, cachedUrls.size, queryClient]);

  let text = "";
  switch (color) {
    case "red":
      text = error || "Instalando Modo Offline";
      break;
    case "yellow":
      text = error || "Preparando Modo Offline";
      break;
    case "green":
      text = isOfflinePlaying
        ? `Você está no Modo Offline (${reason})`
        : "Modo Offline Disponível";
      break;
  }

  const offlineAudiosQuery = useOfflineAudiosQuery(radio);

  function afterAddToCache(eventData: MessageFromServiceWorker) {
    if (eventData.type !== "RESPONSE_ADD_TO_CACHE") return;

    const cachedUrls = new Set(eventData.payload.cachedUrls);
    useOfflineModeState.setState({ cachedUrls });

    if (cachedUrls.size >= 25) {
      useOfflineModeState.setState({
        color: "green",
        error: "",
      });
      setMonitoringConnection(true);
    } else if (cachedUrls.size > 0) {
      useOfflineModeState.setState({
        color: "yellow",
        error: "Modo Offline Parcialmente Disponível",
      });
      setMonitoringConnection(true);
    } else {
      useOfflineModeState.setState({
        color: "red",
        error: "Modo Offline Indisponível (B)",
      });
    }
  }

  function onPong(eventData: MessageFromServiceWorker) {
    if (eventData.type !== "PONG") return;

    if (useMeuPlayState.getState().didPong) return;
    useMeuPlayState.setState({ didPong: true });

    exchangeMessage({
      idRadio: radio.name,
      type: "PRECACHE",
    }).then(() => {
      console.log("[PRECACHE] Precaching complete.");

      const manifest = document.querySelector('link[rel="manifest"]');
      const defaultManifestUrl = getManifestUrl();
      const manifestUrl = manifest?.getAttribute("href") ?? defaultManifestUrl;
      const host = getHost();
      exchangeMessage({
        idRadio: radio.name,
        type: "ADD_TO_PRECACHE",
        payload: { urls: [`${host}${manifestUrl}`] },
      }).then(() => {
        console.log(`[ADD_TO_PRECACHE] ${manifestUrl} precaching complete.`);
      });
    });

    console.log("Received PONG from service worker");
    useOfflineModeState.setState({ color: "yellow" });
    checkConnection(
      () => {
        // online
        console.log("Online; asking Service Worker to add to cache");
        if (radio.offline_mode) {
          offlineAudiosQuery.refetch().then(({ data }) => {
            if (data?.cacheableAudioLinks) {
              const urlsToCache = data.cacheableAudioLinks.slice(0, 25);
              exchangeMessage({
                idRadio: radio.name,
                type: "ADD_TO_CACHE",
                payload: {
                  cacheName: "offlineAudios",
                  urls: urlsToCache,
                },
              }).then(afterAddToCache);
            }
          });
        } else {
          console.log("Offline mode is disabled.");
          useOfflineModeState.setState({ color: "red" });
          exchangeMessage({
            idRadio: radio.name,
            type: "GET_CACHED_URLS",
            payload: { cacheName: "offlineAudios" },
          }).then((response) => {
            if (response.type !== "RESPONSE_GET_CACHED_URLS") {
              return;
            }
            const cachedUrls = response.payload.cachedUrls;
            if (cachedUrls.length > 0) {
              console.log(
                `Removing ${cachedUrls.length} audios from cache....`,
              );
              exchangeMessage({
                idRadio: radio.name,
                type: "REMOVE_FROM_CACHE",
                payload: {
                  cacheName: "offlineAudios",
                  urls: cachedUrls,
                },
              }).then(() => console.log("Audios removed from cache."));
            } else {
              console.log("No audios to remove from cache.");
            }
          });
        }

        // aproveita que tem internet para armazenar áudios que poderão ser tocados sem internet
        player.preloadAudios(radio.name, ["/offline_spot.mp3"]);
      },
      () => {
        // offline
        console.log("Offline; asking Service Worker what is cached");
        exchangeMessage({
          idRadio: radio.name,
          type: "ADD_TO_CACHE",
          payload: {
            cacheName: "offlineAudios",
            urls: [],
          },
        }).then(afterAddToCache);
      },
    );
  }

  // Retry ADD_TO_CACHE a cada 2 min enquanto amarelo para tentar atingir 25 faixas (verde)
  useEffect(() => {
    if (color !== "yellow" || !radio.offline_mode) return;
    const interval = setInterval(() => {
      if (!useOfflineModeState.getState().isOnline) return;
      const { cachedUrls } = useOfflineModeState.getState();
      const missing = 25 - cachedUrls.size;
      if (missing <= 0) return;
      offlineAudiosQuery.refetch().then(({ data }) => {
        if (data?.cacheableAudioLinks) {
          const urlsToCache = data.cacheableAudioLinks
            .filter((url) => !cachedUrls.has(url))
            .slice(0, missing);
          if (urlsToCache.length > 0) {
            console.log(
              `[yellow retry] Tentando cachear mais ${urlsToCache.length} faixa(s)`,
            );
            exchangeMessage({
              idRadio: radio.name,
              type: "ADD_TO_CACHE",
              payload: { cacheName: "offlineAudios", urls: urlsToCache },
            }).then(afterAddToCache);
          }
        }
      });
    }, 2 * 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, radio.name, radio.offline_mode]);

  useEffect(() => {
    upsertRadioSession();
    if (color !== "red") return;
    const interval = setInterval(() => {
      if (useOfflineModeState.getState().color === "red") {
        // console.log("Sending PING to service worker");
        exchangeMessage({
          idRadio: radio.name,
          type: "PING",
        }).then(onPong, () => clearInterval(interval));
      }
    }, 1_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  return (
    <div className="OfflineModeIndicator-component">
      <Indicator
        color={radio.offline_mode ? color : "red"}
        text={radio.offline_mode ? text : "Modo Offline Desativado"}
      />
      <ProxyIndicator />
      <LinkChanger />
      {devMode && <TestButtons />}
    </div>
  );
}
